import { useEffect, useState } from 'react';
import { ImageOff, Plus, X } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import { Button, MultiSelectModal, Skeleton, toast } from '@/components/ui';
import { SortableList } from '@/features/cms/components/SortableList';
import {
  fetchProductOptions,
  resolveProductOption,
} from '../../components/productSelectLoaders';
import {
  useCategoryProductsMutations,
  useCategoryProductsQuery,
  type CategoryProduct,
} from '../hooks/useCategoryProducts';
import type { AdminCategoryView } from '../hooks/useAdminCategoriesQuery';

export interface CategoryProductsPanelProps {
  category: AdminCategoryView;
}

/**
 * J-1/J-2 (ID43) [연결 상품 관리] — 카테고리에 매핑된 상품 다중 추가/제외 + 드래그앤드롭 순서 변경.
 * 기존 ↑↓ 핸들을 @dnd-kit 기반 SortableList(전시·콘텐츠 ID44/45 공통 컴포넌트)로 전환했다.
 * 드래그는 로컬 순서만 즉시 교체하고, [순서 저장] 클릭 시 최종 시퀀스를 PUT /order 로 적용한다
 * (배너 목록과 달리 저장 버튼 명시 — 기존 카테고리 패널 UX·saveOrder 계약 유지).
 */
export const CategoryProductsPanel = ({ category }: CategoryProductsPanelProps) => {
  const { t } = useTranslation('common');
  const categoryId = category.categoryId;
  const query = useCategoryProductsQuery(categoryId);
  const { addProducts, removeProduct, saveOrder } = useCategoryProductsMutations(categoryId);

  const [order, setOrder] = useState<CategoryProduct[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  // 서버 목록이 바뀌면(추가/삭제 후 invalidate 포함) 로컬 순서를 재시드.
  useEffect(() => {
    if (query.data) setOrder(query.data);
  }, [query.data]);

  const dirty =
    query.data != null &&
    (order.length !== query.data.length ||
      order.some((p, i) => p.productId !== query.data![i]?.productId));

  const handleReorder = (next: CategoryProduct[]) => setOrder(next);

  const handleRemove = (productId: string) => {
    removeProduct.mutate(productId, {
      onError: (e) => toast.error(friendlyMessage(e, t('cms.categories.products.removeError', '상품 제외에 실패했습니다.'))),
    });
  };

  const handleAdd = (productIds: ReadonlyArray<string>) => {
    const existing = new Set(order.map((p) => p.productId));
    const toAdd = productIds.filter((id) => !existing.has(id));
    if (toAdd.length === 0) return;
    addProducts.mutate(toAdd, {
      onError: (e) => toast.error(friendlyMessage(e, t('cms.categories.products.addError', '상품 추가에 실패했습니다.'))),
    });
  };

  const handleSaveOrder = () => {
    saveOrder.mutate(
      order.map((p) => p.productId),
      {
        onSuccess: () => toast.success(t('cms.categories.products.orderSaved', '노출 순서를 저장했습니다.')),
        onError: (e) => toast.error(friendlyMessage(e, t('cms.categories.products.orderError', '순서 저장에 실패했습니다.'))),
      },
    );
  };

  return (
    <div
      className="flex flex-col gap-3 rounded-lg p-4"
      style={{ background: 'var(--surface-raised)', border: '1px solid var(--divider-strong)' }}
    >
      <div className="flex items-center justify-between gap-3">
        <h4 className="text-[14px] font-semibold" style={{ color: 'var(--content-strong)' }}>
          {t('cms.categories.products.title', '연결 상품 관리')}
          <span className="ml-2 text-[12px] font-normal" style={{ color: 'var(--content-muted)' }}>
            {category.name}
          </span>
        </h4>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setPickerOpen(true)} disabled={addProducts.isPending}>
            <Plus size={14} aria-hidden="true" />
            {t('cms.categories.products.add', '상품 추가')}
          </Button>
          <Button
            type="button"
            variant="cta"
            size="sm"
            onClick={handleSaveOrder}
            disabled={!dirty || saveOrder.isPending}
          >
            {saveOrder.isPending
              ? t('cms.categories.products.savingOrder', '저장 중…')
              : t('cms.categories.products.saveOrder', '순서 저장')}
          </Button>
        </div>
      </div>

      {query.isPending ? (
        <div className="space-y-2" role="status" aria-live="polite">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-[var(--density-default-row-h)] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          className="rounded-md py-6 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          {t('cms.categories.products.loadFailed', '연결 상품을 불러오지 못했습니다.')}
        </div>
      ) : order.length === 0 ? (
        <div
          className="flex items-center justify-center rounded-md py-8 text-[13px]"
          style={{ background: 'var(--surface-0)', border: '1px dashed var(--divider-strong)', color: 'var(--content-muted)' }}
        >
          {t('cms.categories.products.empty', '연결된 상품이 없습니다.')}
        </div>
      ) : (
        <>
          <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
            {t('cms.categories.products.dragHint', '행 왼쪽 손잡이를 드래그하여 노출 순서를 변경한 뒤 [순서 저장]을 누르세요.')}
          </p>
          <SortableList
            items={order}
            getId={(product) => product.productId}
            onReorder={handleReorder}
            disabled={saveOrder.isPending}
            handleLabel={t('cms.categories.products.dragHandleLabel', '연결 상품 순서 이동 핸들')}
            className="flex flex-col gap-1.5"
          >
            {(product, idx, dragHandle) => (
              <div
                className="flex items-center gap-3 rounded-md px-3 py-2"
                style={{ background: 'var(--surface-1)', border: '1px solid var(--divider-strong)' }}
              >
                {dragHandle}
                <span className="w-6 shrink-0 text-center text-[12px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
                  {idx + 1}
                </span>
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded"
                  style={{ background: 'var(--surface-0)', border: '1px solid var(--divider-strong)' }}
                >
                  {product.thumbnailUrl ? (
                    <img src={product.thumbnailUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
                  ) : (
                    <ImageOff size={14} style={{ color: 'var(--content-soft)' }} aria-hidden="true" />
                  )}
                </div>
                <span className="min-w-0 flex-1 truncate text-[14px]" style={{ color: 'var(--content-strong)' }} title={product.name}>
                  {product.name}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="shrink-0"
                  disabled={removeProduct.isPending}
                  onClick={() => handleRemove(product.productId)}
                  aria-label={t('cms.categories.products.remove', { name: product.name, defaultValue: '제외' })}
                >
                  <X size={14} style={{ color: 'var(--signal-danger)' }} />
                </Button>
              </div>
            )}
          </SortableList>
        </>
      )}

      <MultiSelectModal
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        loadOptions={fetchProductOptions}
        resolveOption={resolveProductOption}
        initialValue={[]}
        onApply={handleAdd}
        title={t('cms.categories.products.pickerTitle', '연결할 상품 선택')}
        description={t('cms.categories.products.pickerDescription', '이 카테고리에 추가할 상품을 다중 선택하세요.')}
        searchPlaceholder={t('cms.categories.products.pickerSearch', '상품명으로 검색')}
        applyLabel={t('cms.categories.products.pickerApply', '선택 추가')}
      />
    </div>
  );
};
